import { Component, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ApplicationConfigurationSetting } from '@rocksoft/mdm-sdk-v2';

@Component({
  selector: 'ml-mdm-application-configuration',
  templateUrl: './application-configuration.component.html',
  styleUrls: ['./application-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class ApplicationConfigurationComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly applicationConfiguration = input.required<ApplicationConfigurationSetting>();
  readonly mutate = input(false);
  readonly allowToggle = input(true);
  readonly integrated = input(true);

  readonly applicationConfigurationChange = output<ApplicationConfigurationSetting>();

  editing = false;

  public applicationConfigurationForm: UntypedFormGroup;

  constructor() {
    this.createForm();

    effect(() => {
      const applicationConfiguration = this.applicationConfiguration();
      this.rebuildForm(applicationConfiguration);
    });
  }

  get configuration(): UntypedFormArray {
    return this.applicationConfigurationForm.get('configuration') as UntypedFormArray;
  }

  get configurationKeysSorted(): string[] {
    const applicationConfiguration = this.applicationConfiguration();
    if (!applicationConfiguration?.configuration) {
      return [];
    }
    return Object.keys(applicationConfiguration.configuration).sort();
  }

  addConfiguration(): void {
    this.configuration.push(this.formBuilder.group({ id: '', value: '' }));
  }

  deleteConfiguration(index: number): void {
    this.configuration.removeAt(index);
  }

  doneEditing(): boolean {
    if (!this.editing) {
      return true;
    }

    if (this.applicationConfigurationForm.status !== 'VALID') {
      return false;
    }

    const result = this.populate();
    this.editing = false;
    this.applicationConfigurationChange.emit(result);

    return true;
  }

  toggleEditing(): void {
    // Toggle if we are not editing, or if the edited form validates
    if (!this.editing || this.doneEditing()) {
      this.editing = !this.editing;
    }
  }

  private createForm(): void {
    this.applicationConfigurationForm = this.formBuilder.group({
      identifier: ['', Validators.required],
      configuration: this.formBuilder.array([]),
    });
  }

  private rebuildForm(applicationConfiguration: ApplicationConfigurationSetting): void {
    this.applicationConfigurationForm.reset({
      identifier: applicationConfiguration?.identifier || null,
    });
    const configuration = applicationConfiguration?.configuration || null;
    this.setConfiguration(configuration ?? {});
  }

  private setConfiguration(configuration: Record<string, string>) {
    const configurationGroups = Object.keys(configuration || []).map((key) => {
      const value = configuration[key];
      return this.formBuilder.group({ id: key, value });
    });

    const configurationFormArray = this.formBuilder.array(configurationGroups);
    this.applicationConfigurationForm.setControl('configuration', configurationFormArray);
  }

  private populate(): ApplicationConfigurationSetting {
    const formModel = this.applicationConfigurationForm.value;
    let applicationConfiguration = this.applicationConfiguration();
    if (!this.mutate()) {
      applicationConfiguration = {
        configuration: this.applicationConfiguration().configuration,
        identifier: this.applicationConfiguration().identifier,
        item: this.applicationConfiguration().item,
      };
    }

    // Set default values
    applicationConfiguration.item = this.applicationConfiguration().item;
    applicationConfiguration.identifier = this.applicationConfiguration().identifier;

    const configuration = {};
    formModel.configuration.forEach((item) => (configuration[item.id] = item.value));

    applicationConfiguration.configuration = configuration;

    return applicationConfiguration;
  }
}
